import { PaceTaskScreen } from '@symfa-inc/providence-verizon-types';
import {
  DatesAtAGlanceRow,
  OverdueTaskRow,
  ProjectDurationRow,
  StructuralPassFailRow,
} from '@models/interfaces';
import { INTERACTIVE_ROW_CLICK_SELECTOR } from './constants';

interface ReportRowRedirectConfig {
  getUrl: (projectBundleID: number) => string;
  screens: string[];
}

interface ReportRowScreensSource {
  paceTaskScreen?: string | null;
  paceTaskScreens?: string[] | null;
  paceTaskScreensDisplay?: string | null;
}

const SUMMARY_TAB_SEARCH = '?tabName=Summary';
const WORKFLOW_TAB_SEARCH = '?tabName=Workflow';

const getDetailsSummaryUrl = (
  modulePath: string,
  projectBundleID: number,
): string => `/${modulePath}/${projectBundleID}/details${SUMMARY_TAB_SEARCH}`;

export const REPORT_ROW_REDIRECT_PRIORITY: ReportRowRedirectConfig[] = [
  {
    getUrl: (projectBundleID: number): string =>
      getDetailsSummaryUrl('accounting', projectBundleID),
    screens: [PaceTaskScreen.Accounting],
  },
  {
    getUrl: (projectBundleID: number): string =>
      getDetailsSummaryUrl('project', projectBundleID),
    screens: [PaceTaskScreen.Project],
  },
  {
    getUrl: (projectBundleID: number): string =>
      getDetailsSummaryUrl('engineering', projectBundleID),
    screens: [PaceTaskScreen.Engineering],
  },
  {
    getUrl: (projectBundleID: number): string =>
      getDetailsSummaryUrl('leasing', projectBundleID),
    screens: [PaceTaskScreen.Leasing],
  },
  {
    getUrl: (projectBundleID: number): string =>
      getDetailsSummaryUrl('permitting', projectBundleID),
    screens: [PaceTaskScreen.Permitting],
  },
  {
    getUrl: (projectBundleID: number): string =>
      getDetailsSummaryUrl('project-management', projectBundleID),
    screens: [PaceTaskScreen.ProjectManagement, 'Project Management'],
  },
  {
    getUrl: (projectBundleID: number): string =>
      getDetailsSummaryUrl('project', projectBundleID),
    screens: [PaceTaskScreen.Forecasting],
  },
];

export const openReportRowInNewTab = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const isInteractiveRowClick = (event?: {
  target?: EventTarget | null;
}): boolean => {
  const target = event?.target;

  return (
    target instanceof HTMLElement &&
    !!target.closest(INTERACTIVE_ROW_CLICK_SELECTOR)
  );
};

export const normalizePaceTaskScreen = (screen?: string | null): string =>
  typeof screen === 'string'
    ? screen.replace(/[^a-z0-9]/gi, '').toLowerCase()
    : '';

export const getReportRowScreens = ({
  paceTaskScreen,
  paceTaskScreens,
  paceTaskScreensDisplay,
}: ReportRowScreensSource): string[] =>
  [
    ...(paceTaskScreens ?? []),
    paceTaskScreen,
    ...(paceTaskScreensDisplay?.split(/[,/|]+/) ?? []),
  ].filter(Boolean) as string[];

export const getOverdueRowScreens = (record: OverdueTaskRow): string[] =>
  getReportRowScreens(record);

export const getDatesAtAGlanceRowScreens = (
  record: DatesAtAGlanceRow,
): string[] => getReportRowScreens(record);

export const getRedirectConfigByPaceTaskScreens = (
  screens: string[],
): ReportRowRedirectConfig | undefined => {
  const normalizedScreens = new Set(screens.map(normalizePaceTaskScreen));

  return REPORT_ROW_REDIRECT_PRIORITY.find(config =>
    config.screens.some((screen: string) =>
      normalizedScreens.has(normalizePaceTaskScreen(screen)),
    ),
  );
};

const getReportRowRedirectUrlByScreens = (
  projectBundleID: number,
  screens: string[],
): string => {
  const redirectConfig = getRedirectConfigByPaceTaskScreens(screens);

  if (redirectConfig?.getUrl) {
    return redirectConfig.getUrl(projectBundleID);
  }

  return getDetailsSummaryUrl('project', projectBundleID);
};

export const getReportRowRedirectUrl = (
  record: OverdueTaskRow,
): string | undefined => {
  if (!record.projectBundleID) {
    return undefined;
  }

  return getReportRowRedirectUrlByScreens(
    record.projectBundleID,
    getOverdueRowScreens(record),
  );
};

export const getDatesAtAGlanceRowRedirectUrl = (
  record: DatesAtAGlanceRow,
): string | undefined =>
  record.projectBundleID
    ? getReportRowRedirectUrlByScreens(
        record.projectBundleID,
        getDatesAtAGlanceRowScreens(record),
      )
    : undefined;

export const getStructuralPassFailRowRedirectUrl = ({
  projectBundleID,
}: StructuralPassFailRow): string | undefined =>
  projectBundleID
    ? `/leasing/${projectBundleID}/details${WORKFLOW_TAB_SEARCH}`
    : undefined;

export const getProjectDurationRowRedirectUrl = ({
  projectBundleID,
}: ProjectDurationRow): string | undefined =>
  projectBundleID
    ? getDetailsSummaryUrl('project', projectBundleID)
    : undefined;
